'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { SearchX } from 'lucide-react';
import ProductsList from './products-list';
import ProductsListSkeleton from './products-list-skeleton';
import ContentPagination from '../content-pagination';
import { Separator } from '@/components/ui/separator';
import { ProductType, ProductsSortByEnum } from '@/types/product';
import { SortEnum } from '@/enums';
import { buildCategoryPath, getLocalizedValue, buildProductPath } from '@/lib/utils';
import { fetchProducts } from '@/lib/api';
import { transformSortByForBackend } from '@/lib/product-utils';
import { PAGINATION_DEFAULT_SIZE } from '@/constants';
import { useProductsLoadingStore } from '@/store';
const DEFAULT_SORT_BY = ProductsSortByEnum.CREATED_AT;
const DEFAULT_SORT = SortEnum.DESC;
type ProductWithHref = ProductType & {
  detailHref: string;
};
type FilterParams = {
  categoryIds?: string[];
  vehicleGroupIds?: string[];
  brandIds?: string[];
  modelIds?: string[];
  q?: string;
};
type PropsType = {
  initialItems: ProductWithHref[];
  initialTotal: number;
  initialPages: number;
  page: number;
  size: number;
  sortBy: ProductsSortByEnum;
  sort: SortEnum;
  backendLocale: string;
  // Translated "product" route segment (no leading slash), used to build detail links client-side.
  productRouteBase: string;
  isAuthenticated: boolean;
  // Category/filter scope that stays fixed while paginating/sorting client-side.
  filterParams: FilterParams;
};
type ProductsQueryResult = {
  items: ProductWithHref[];
  total: number;
  pages: number;
};

/**
 * Client-side products' browser. Initial data comes from SSR (so the first paint and crawlers get
 * fully-rendered products), then pagination / sort / page-size changes fetch just the product data
 * via React Query and update the grid in place — without a full server navigation or page skeleton.
 * The URL is kept in sync via the History API so pages stay shareable and back/forward works.
 */
const ProductsResults = t0 => {
  const $ = _c(67);
  const {
    initialItems,
    initialTotal,
    initialPages,
    page: initialPage,
    size: initialSize,
    sortBy: initialSortBy,
    sort: initialSort,
    backendLocale,
    productRouteBase,
    isAuthenticated,
    filterParams
  } = t0;
  const t = useTranslations();
  const searchParams = useSearchParams();
  const navPending = useProductsLoadingStore(_temp);
  const setNavPending = useProductsLoadingStore(_temp2);
  let t1;
  let t2;
  if ($[0] !== setNavPending) {
    t1 = () => {
      setNavPending(false);
    };
    t2 = [setNavPending];
    $[0] = setNavPending;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const [page, setPage] = useState(initialPage);
  const [size, setSize] = useState(initialSize);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sort, setSort] = useState(initialSort);
  const [mountedAt] = useState(_temp3);
  let t3;
  if ($[3] !== backendLocale || $[4] !== productRouteBase) {
    t3 = product => {
      const slug = getLocalizedValue(product, "slug", backendLocale);
      const categoryPath = product.category ? buildCategoryPath(product.category, backendLocale) : "";
      const base = "/" + [productRouteBase, categoryPath, slug].filter(Boolean).join("/");
      return buildProductPath(base, product);
    };
    $[3] = backendLocale;
    $[4] = productRouteBase;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const buildHref = t3;
  let t4;
  if ($[6] !== backendLocale || $[7] !== sortBy) {
    t4 = transformSortByForBackend(sortBy, backendLocale);
    $[6] = backendLocale;
    $[7] = sortBy;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== filterParams || $[10] !== page || $[11] !== size || $[12] !== sort || $[13] !== t4) {
    t5 = {
      ...filterParams,
      page,
      size,
      sortBy: t4,
      sort,
      active: "true"
    };
    $[9] = filterParams;
    $[10] = page;
    $[11] = size;
    $[12] = sort;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const queryParams = t5;
  const isInitialQuery = page === initialPage && size === initialSize && sortBy === initialSortBy && sort === initialSort;
  let t6;
  if ($[15] !== backendLocale || $[16] !== queryParams) {
    t6 = ["products-browser", backendLocale, queryParams];
    $[15] = backendLocale;
    $[16] = queryParams;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== buildHref || $[19] !== queryParams) {
    t7 = async () => {
      const json = await fetchProducts(queryParams);
      return {
        items: (json.items ?? []).map(product_0 => ({
          ...product_0,
          detailHref: buildHref(product_0)
        })),
        total: json.total ?? 0,
        pages: json.pages ?? 1
      };
    };
    $[18] = buildHref;
    $[19] = queryParams;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== initialItems || $[22] !== initialPages || $[23] !== initialTotal || $[24] !== isInitialQuery) {
    t8 = isInitialQuery ? {
      items: initialItems,
      total: initialTotal,
      pages: initialPages
    } : undefined;
    $[21] = initialItems;
    $[22] = initialPages;
    $[23] = initialTotal;
    $[24] = isInitialQuery;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = isInitialQuery ? mountedAt : undefined;
  let t10;
  if ($[26] !== t6 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = {
      queryKey: t6,
      queryFn: t7,
      initialData: t8,
      initialDataUpdatedAt: t9,
      placeholderData: keepPreviousData,
      staleTime: 60000
    };
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const {
    data,
    isFetching
  } = useQuery(t10);
  const syncUrl = _temp4;
  let t11;
  let t12;
  if ($[31] !== searchParams) {
    t11 = () => {
      setPage(Number(searchParams.get("page") ?? "1") || 1);
      setSize(Number(searchParams.get("size") ?? String(PAGINATION_DEFAULT_SIZE)) || PAGINATION_DEFAULT_SIZE);
      setSortBy(searchParams.get("sortBy") as ProductsSortByEnum || DEFAULT_SORT_BY);
      setSort(searchParams.get("sort") as SortEnum || DEFAULT_SORT);
    };
    t12 = [searchParams];
    $[31] = searchParams;
    $[32] = t11;
    $[33] = t12;
  } else {
    t11 = $[32];
    t12 = $[33];
  }
  useEffect(t11, t12);
  let t13;
  if ($[34] !== size) {
    t13 = (nextSortBy, nextSort) => {
      setSortBy(nextSortBy);
      setSort(nextSort);
      setPage(1);
      syncUrl({
        page: 1,
        size,
        sortBy: nextSortBy,
        sort: nextSort
      });
    };
    $[34] = size;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const handleSortChange = t13;
  let t14;
  if ($[36] !== sort || $[37] !== sortBy) {
    t14 = nextSize => {
      setSize(nextSize);
      setPage(1);
      syncUrl({
        page: 1,
        size: nextSize,
        sortBy,
        sort
      });
    };
    $[36] = sort;
    $[37] = sortBy;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const handleSizeChange = t14;
  let t15;
  if ($[39] !== size || $[40] !== sort || $[41] !== sortBy) {
    t15 = nextPage => {
      setPage(nextPage);
      syncUrl({
        page: nextPage,
        size,
        sortBy,
        sort
      });
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    };
    $[39] = size;
    $[40] = sort;
    $[41] = sortBy;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const handlePageChange = t15;
  let t16;
  if ($[43] !== data?.items) {
    t16 = data?.items ?? [];
    $[43] = data?.items;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const items = t16;
  const total = data?.total ?? 0;
  const totalPages = data?.pages ?? 1;
  const showSkeleton = isFetching || navPending;
  if (items.length === 0 && !showSkeleton) {
    let t17;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div className="bg-muted mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"><SearchX className="text-muted-foreground h-8 w-8" /></div>;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    let t18;
    if ($[46] !== t) {
      t18 = t("no_products_found");
      $[46] = t;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    let t19;
    if ($[48] !== t18) {
      t19 = <div className="bg-card/50 flex min-h-100 items-center justify-center rounded-xl border border-dashed"><div className="text-center">{t17}<p className="text-muted-foreground text-lg font-medium">{t18}</p></div></div>;
      $[48] = t18;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    return t19;
  }
  let t17;
  if ($[50] !== handleSizeChange || $[51] !== handleSortChange || $[52] !== isAuthenticated || $[53] !== items || $[54] !== showSkeleton || $[55] !== size || $[56] !== sort || $[57] !== sortBy || $[58] !== total) {
    t17 = showSkeleton ? <ProductsListSkeleton rows={size} /> : <ProductsList items={items} total={total} size={size} isAuthenticated={isAuthenticated} sortBy={sortBy} sort={sort} onSortChange={handleSortChange} onSizeChange={handleSizeChange} />;
    $[50] = handleSizeChange;
    $[51] = handleSortChange;
    $[52] = isAuthenticated;
    $[53] = items;
    $[54] = showSkeleton;
    $[55] = size;
    $[56] = sort;
    $[57] = sortBy;
    $[58] = total;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== handlePageChange || $[61] !== page || $[62] !== totalPages) {
    t18 = totalPages > 1 && <div className="mt-5 flex flex-col gap-5"><Separator /><ContentPagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} /></div>;
    $[60] = handlePageChange;
    $[61] = page;
    $[62] = totalPages;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== t17 || $[65] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  return t19;
};
export default ProductsResults;
function _temp(state) {
  return state.pending;
}
function _temp2(state_0) {
  return state_0.setPending;
}
function _temp3() {
  return Date.now();
}
function _temp4(next) {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  if (next.size === PAGINATION_DEFAULT_SIZE) {
    url.searchParams.delete("size");
  } else {
    url.searchParams.set("size", String(next.size));
  }
  if (next.sortBy === DEFAULT_SORT_BY && next.sort === DEFAULT_SORT) {
    url.searchParams.delete("sortBy");
    url.searchParams.delete("sort");
  } else {
    url.searchParams.set("sortBy", next.sortBy);
    url.searchParams.set("sort", next.sort);
  }
  if (next.page > 1) {
    url.searchParams.set("page", String(next.page));
  } else {
    url.searchParams.delete("page");
  }
  window.history.pushState(null, "", url.toString());
}